import React, { useCallback, useState, useMemo } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl } from '..';
import { bodyAreas, quoteInitialValues as initialValues, quoteSchema as validationSchema } from '.';
import { buildQuoteDescription } from '../../../utils/formSchemas';
import { useFormPrePopulation } from '../../../hooks/useFormPrePopulation.ts';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
    callVerifyHuman,
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    GetEmailTemplate,
    handleSendEmail,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    getLeadsCaptureEcho,
    safeFullStory as FullStory,
} from '../../../utils';
import axios from 'axios';
import { navigate } from 'gatsby';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    LocationsDropdownWrapper,
    MarketingConsentMsg,
} from '../shared';
import { useScrollToAnchor } from '../../../hooks/builder/useScrollToAnchor';

export function QuoteForm({ siteData }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Pre-populate form from localStorage if autofill parameter is present
    const { initialFormValues } = useFormPrePopulation(initialValues);

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('customQuote', executeRecaptcha);
        return user;
    }, [executeRecaptcha]);

    // Handle form submission (memoized to prevent formikConfig recreation)
    const handleSubmit = useCallback(
        async (values, helpers) => {
            scrollToAnchor('quote-form-container');
            helpers.setSubmitting(true);
            const { fbp, fbc } = getFacebookCookies();
            const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
            try {
                const user = await handleReCaptchaVerify();
                if (user === 'bot') {
                    helpers.setErrors('bot', true);
                    throw new Error('Bot detected');
                } else {
                    // Validate phone and email with PossibleNow
                    const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                        values.phone,
                        values.email
                    );

                    const formData = {
                        ...values,
                        updates: true, // Always true for TCPA compliance
                        description: buildQuoteDescription(values.bodyAreas, values.description),
                        cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                        formType: 'quote',
                        fbp,
                        fbc,
                        experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                        experiment_user_ID,
                        phoneFound,
                        wireless,
                        emailFound,
                        result,
                        flags,
                        marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                    };

                    const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

                    // Store form values for the attribution survey. The handler returns
                    // submissionId and TU IDs when leads-capture is on; the survey echoes them so
                    // its answer updates the original lead instead of creating a second one.
                    setFormValues({ ...formData, ...getLeadsCaptureEcho(response) });

                    // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                    if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                        syncPardotTracking(values);
                    }

                    // Show attribution survey instead of immediate success
                    setShowAttributionSurvey(true);

                    helpers.setSubmitting(false);
                    helpers.setStatus('Success');
                    // FullStory Event Tracking
                    FullStory('trackEvent', {
                        name: 'Custom Quote Form - Success',
                        properties: {
                            name: 'Custom Quote',
                            user_email: values.email,
                            sms_optin: CTA_CLICK_SMS_OPT_IN,
                        },
                    });

                    // For CQ, we set dataLayer variable here so GTM can pass it in the custom_quote GA4 event
                    window.dataLayer?.push({ sms_optin: CTA_CLICK_SMS_OPT_IN });

                    // Clear localStorage data after successful submission
                    if (typeof window !== 'undefined') {
                        window.localStorage.removeItem('form_data');
                    }

                    const htmlTemplateForCustomQuote = GetEmailTemplate.customQuoteEmailTemplate(
                        values.first_name,
                        siteData.selectedStore.metro.name
                    );
                    handleSendEmail(
                        values.email,
                        'Milan Laser Custom Quote',
                        htmlTemplateForCustomQuote,
                        'Custom Quote'
                    );
                    navigate('?quote-form-success'); // This is for Marketing
                    helpers.resetForm();
                }
            } catch (error) {
                console.error('Form submission error:', error);
                const errorMessage = error instanceof Error ? error.message : 'Form submission error';

                helpers.setSubmitting(false);
                helpers.setStatus('Failed');
                setFormValues(null);
                setShowAttributionSurvey(false);

                // FullStory Event Tracking with enhanced error details
                FullStory('trackEvent', {
                    name: 'Custom Quote Form - Error',
                    properties: {
                        name: 'Custom Quote',
                        user_email: values.email,
                        error_message: errorMessage,
                        error_details: axios.isAxiosError(error)
                            ? {
                                  status: error.response?.status,
                                  statusText: error.response?.statusText,
                              }
                            : 'Non-axios error',
                    },
                });

                // Log that Pardot tracking was skipped due to form submission failure
                FullStory('trackEvent', {
                    name: 'Custom Quote Form - Pardot Sync Skipped',
                    properties: {
                        user_email: values.email,
                        error_message: errorMessage,
                        reason: 'Actual Form submission failed',
                    },
                });
            }
        },
        [executeRecaptcha, handleReCaptchaVerify, scrollToAnchor, siteData]
    );

    const handleAttributionComplete = useCallback(() => {
        setShowAttributionSurvey(false);

        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    }, []);

    // Memoize Formik configuration to prevent unnecessary re-renders
    const formikConfig = useMemo(
        () => ({
            initialValues: initialFormValues,
            validationSchema,
            onSubmit: handleSubmit,
            validateOnChange: false,
            enableReinitialize: true, // Allow form to reinitialize when initialValues change
        }),
        [initialFormValues, handleSubmit]
    );

    return (
        <div id="quote-form-container" className="quote-form-container" style={{ minHeight: '300px' }}>
            <Formik {...formikConfig}>
                {(formik) => {
                    if (formik.isSubmitting) {
                        return <FormSubmitting />;
                    }

                    // Handle error state
                    if (formik.status === 'Failed') {
                        return <FormFailed />;
                    }

                    {
                        /* At this point the form is submitted */
                    }
                    if (formValues) {
                        if (showAttributionSurvey) {
                            return (
                                <AttributionSurvey
                                    formValues={formValues}
                                    onComplete={handleAttributionComplete}
                                    isFullWidth={false}
                                />
                            );
                        }
                        return <FormSuccess />;
                    }

                    return (
                        <Form id="quote-form" className="quote-form w-100 py-4 fs-mask">
                            {!showAttributionSurvey && !formValues && !formik.isSubmitting && (
                                <h2 className="milan-text-primary h3 fw-normal text-center">
                                    Fill out the form below to request your quote.
                                </h2>
                            )}
                            {/* Form content remains the same */}
                            <div className="form-inputs-wrapper pt-4">
                                <div className="row gy-3 justify-content-center mx-auto">
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="input"
                                            name="first_name"
                                            placeholder="First Name"
                                            type="text"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="input"
                                            name="last_name"
                                            placeholder="Last Name"
                                            type="text"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                </div>
                                <div className="row gy-3 justify-content-center mx-auto pt-3">
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="phoneInput"
                                            name="phone"
                                            placeholder="Phone Number"
                                            type="tel"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <FormikControl
                                            control="emailInput"
                                            name="email"
                                            placeholder="Your Email"
                                            role="input"
                                            aria-required
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <label htmlFor="location">Location</label>
                                        <LocationsDropdownWrapper {...{ siteData, formik }} fieldName="location" />
                                    </div>
                                </div>
                                <p className="text-center p-subhead mt-3 mt-sm-4 milan-text-primary">
                                    Please select up to 3 areas you're interested in.
                                </p>
                                <div className="row justify-content-center mt-4 pt-1 checkboxes">
                                    <ul className="col-sm-10 row">
                                        <FormikControl
                                            control="areasCheckboxes"
                                            name="bodyAreas"
                                            options={bodyAreas}
                                            formik={formik}
                                        />
                                    </ul>
                                </div>
                                <div className="row justify-content-center mt-4">
                                    <div className="col-md-10">
                                        <p className="p-subhead text-center milan-text-primary">
                                            Other Areas{' '}
                                            <span className="d-none d-md-inline">
                                                (we treat almost any area of the body)
                                            </span>
                                            :
                                        </p>
                                        <FormikControl
                                            control="textarea"
                                            name="description"
                                            placeholder="Describe any other areas you're interested in."
                                            rows="1"
                                            validateOnChange
                                        />
                                    </div>
                                </div>
                                <div className="row justify-content-center my-3">
                                    <div className="col-md-10 lh-1">
                                        <MarketingConsentMsg
                                            cta={
                                                <>
                                                    Request A Quote <sup>*</sup>
                                                </>
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div id="actions-btns" className="row justify-content-center my-3 col-lg-10 m-auto">
                                <div className="col-sm-6 col-lg-4 text-center">
                                    <button
                                        className="milan-btn milan-red-btn w-100"
                                        type="submit"
                                        disabled={!formik.isValid || formik.isSubmitting}
                                    >
                                        {formik.isSubmitting ? (
                                            'Submitting...'
                                        ) : (
                                            <>
                                                Request A Quote <sup>*</sup>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}

export default QuoteForm;
